import { useCallback, useSyncExternalStore } from 'react';

const mqlCache = new Map<string, MediaQueryList>();

const getMql = (query: string): MediaQueryList | null => {
  if (typeof window === 'undefined') return null;
  const cached = mqlCache.get(query);
  if (cached) return cached;
  const mql = window.matchMedia(query);
  mqlCache.set(query, mql);
  return mql;
};

// Server/first-paint fallback: assume desktop so the rail renders rather than
// a drawer trigger that would not work without JS.
const getServerSnapshot = (): boolean => true;

/**
 * Subscribe to a CSS media query from JS.
 *
 * `useSyncExternalStore` rather than useState + useEffect: the latter renders
 * once with a wrong value and then corrects it, which shows as a visible flash
 * of the desktop layout on a phone. This reads the real value on first render
 * and is tear-free under React 19 concurrent rendering.
 */
export default function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      const mql = getMql(query);
      if (!mql) return () => {};
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback((): boolean => getMql(query)?.matches ?? true, [query]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/*
 * Breakpoints are expressed in `rem`, never px, because Tailwind v4 emits its
 * own media queries in rem (`lg` is 64rem, and our custom `--breakpoint-xs` in
 * styles/globals.css is 30rem). A px query only agrees with them at a 16px root
 * font size; at any other size there is a band where JS says "phone" while CSS
 * says "desktop" (or the reverse) and the user gets neither the drawer trigger
 * nor the rail. `rem` in a media query resolves against the browser's initial
 * font size, exactly as Tailwind's do, so the two can never drift.
 */

/** True at Tailwind's `lg` (64rem) and up, where the persistent rail is shown. */
export const useIsDesktop = (): boolean => useMediaQuery('(min-width: 64rem)');
